import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import {
  getEmitterTypeToEnum,
  getValueTypeFromEmitterType,
  IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
  IRRIGATION_PLANNED_VOLUME_VALUE_TYPE_PREFIX,
} from '@semios/app-platform-values-helpers-and-types'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { TIrrigationDeviceStatus, TValuesCurrentIrrigationZones } from 'App/Map/types'
import type {
  GridTableContentSection,
  GridTableContentSectionItem,
  PossibleValuesRecord,
} from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitter } from 'stores/fieldAssetStore/helperTypes'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TIrrigationZoneValueTypes } from '../../_utils'
import { getRowHeightForLabelCell } from '../_utils/getRowHeightForLabelCell'
import { IrrigationActivityCell } from '../_utils/IrrigationActivityCell/IrrigationActivityCell'
import { IrrigationActivityLabel } from '../_utils/IrrigationActivityLabel/IrrigationActivityLabel'
import { makeLabelForNameAndEmitterType } from '../_utils/makeLabelForNameAndEmitterType'
import { IrrigationActivityDropdown } from '../IrrigationActivityTitleBarChildren/IrrigationActivityDropdown'
import { IrrigationActivityToggleButton } from '../IrrigationActivityTitleBarChildren/IrrigationActivityToggleButton'

type CommonValueType = PossibleValuesRecord & {
  appliedDuration: number | null
  appliedVolume: number | null
  scheduledDuration: number | null
  scheduledVolume: number | null
}

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_DETAILS' })

const getValue = ({
  isDuration,
  duration,
  volume,
}: {
  isDuration: boolean
  duration: number | null
  volume: number | null
}): string | null => {
  if (isNil(duration)) {
    return null
  }

  if (isDuration) {
    const durationMinutes = duration / 60000

    return minutesToHoursAndMinutes(durationMinutes)
  }

  return unitConverter.irrigationActivity(volume ?? 0).valueWithSuffix()
}

const generateItem = ({
  currentValuesData,
  deviceName,
  emitterType,
  id,
  irrigationZoneId,
  values,
}: {
  currentValuesData: routes.ValuesCurrent.Response['irrigation_zones']
  deviceName: string
  emitterType: TEmitterType
  id: string
  irrigationZoneId: number
  values: Record<string, CommonValueType>
}): GridTableContentSectionItem => {
  const noValueDefaultReturn = translate.phrases.templates('-')

  const label = makeLabelForNameAndEmitterType({
    deviceName,
    emitterType,
  })

  const height = getRowHeightForLabelCell({ label, minHeight: 100 })
  const { appliedIrrigationActivityDisplay } = smallStore.getState()
  const isDuration = appliedIrrigationActivityDisplay === 'DURATION'
  const currentValuesForZone = currentValuesData?.[irrigationZoneId]

  let currentValueStatus: TIrrigationDeviceStatus | null = null

  if (currentValuesForZone) {
    const currentValueKey = `irrigationIsOn_${getEmitterTypeToEnum(
      emitterType,
    )}_status` as TValuesCurrentIrrigationZones

    currentValueStatus = currentValuesForZone[currentValueKey]?.value as TIrrigationDeviceStatus
  }

  return {
    id,
    labelString: label,
    label: <IrrigationActivityLabel label={label} currentValueStatus={currentValueStatus} />,
    height,
    labelMinWidth: 200,
    valueMinWidth: 120,
    render: (dataPoint: {
      appliedDuration: number | null
      appliedVolume: number | null
      scheduledDuration: number | null
      scheduledVolume: number | null
    }) => {
      if (!dataPoint) return noValueDefaultReturn

      const { appliedDuration, appliedVolume, scheduledDuration, scheduledVolume } = dataPoint

      const value = {
        applied:
          getValue({ duration: appliedDuration, volume: appliedVolume, isDuration }) ?? noValueDefaultReturn,
        scheduled:
          getValue({ duration: scheduledDuration, volume: scheduledVolume, isDuration }) ??
          noValueDefaultReturn,
      }

      return <IrrigationActivityCell label={label} {...value} />
    },
    values,
  }
}

export const irrigationZonesActivityContent = ({
  data,
  selectedFieldAssets,
  currentValuesData,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  currentValuesData: routes.ValuesCurrent.Response
}): GridTableContentSection => {
  const title = translate.phrases.banyanApp('Irrigation Activity by Zone')

  const commonReturnItems = {
    title,
    fieldAssetPicker: <IrrigationActivityDropdown />,
    id: 'summary-grid-irrigationZones',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const properties = fieldAssetStore.getState().properties ?? {}
  const property = properties[Number(selectedFieldAssets.property)]

  const irrigationZoneEmitters: TIrrigationZoneEmitter[] = Object.values(
    property?.irrigationZoneEmitters || {},
  )

  if (irrigationZoneEmitters.length === 0) {
    return propertyLacksPermissionSectionMaker(commonReturnItems)
  }

  const items = irrigationZoneEmitters
    .map((zoneEmitter) => {
      const { irrigationZoneEmitterId, irrigationZoneId, emitterType } = zoneEmitter

      const appliedValueType = getValueTypeFromEmitterType(
        IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
        emitterType as TEmitterType,
      ) as TIrrigationZoneValueTypes

      const scheduledValueType = getValueTypeFromEmitterType(
        IRRIGATION_PLANNED_VOLUME_VALUE_TYPE_PREFIX,
        emitterType as TEmitterType,
      ) as TIrrigationZoneValueTypes

      const appliedData = Object.values(
        data?.irrigationZones?.[irrigationZoneId]?.values?.[appliedValueType]?.[0]?.timeseries ?? {},
      ).reduce((acc, curr) => {
        acc[curr.timestamp] = curr

        return acc
      }, {})

      const scheduledData = Object.values(
        data?.irrigationZones?.[irrigationZoneId]?.values?.[scheduledValueType]?.[0]?.timeseries ?? {},
      ).reduce((acc, curr) => {
        acc[curr.timestamp] = curr

        return acc
      }, {})

      const allTimestamps = [...new Set([...Object.keys(appliedData), ...Object.keys(scheduledData)])]

      const values = allTimestamps.reduce(
        (acc, timestamp) => {
          const appliedEntry = appliedData[timestamp]
          const scheduledEntry = scheduledData[timestamp]

          acc[timestamp] = {
            timestamp,
            appliedDuration: appliedEntry?.value?.duration,
            appliedVolume: appliedEntry?.value?.volume_mm,
            scheduledDuration: scheduledEntry?.value?.duration,
            scheduledVolume: scheduledEntry?.value?.volume_mm,
          }

          return acc
        },
        {} as Record<
          string,
          {
            timestamp: string
            appliedDuration: number | null
            appliedVolume: number | null
            scheduledDuration: number | null
            scheduledVolume: number | null
          }
        >,
      )

      return generateItem({
        deviceName: zoneEmitter.name,
        emitterType,
        id: irrigationZoneEmitterId,
        irrigationZoneId: Number(irrigationZoneId),
        values,
        currentValuesData: currentValuesData.irrigation_zones,
      })
    })
    .sort(sortByKey('labelString'))
    .filter(Boolean)

  return {
    ...commonReturnItems,
    titleToggle: <IrrigationActivityToggleButton />,
    items,
  }
}
